/**
 * FollowTagDialog — confirmation dialog for following / unfollowing a tag
 * from a clickable tag chip (professional expertise chips on the index and
 * detail pages). Mirrors the organizations pages' followed-tag toggle, but
 * asks first instead of toggling immediately.
 *
 * The dialog is presentational: the caller owns the preference store
 * (useTagPreferences) and performs the toggle in `onConfirm`.
 */
import React from "react";
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Typography,
} from "@mui/material";
import { useAuth } from "@/context/AuthContext";
import {
    getSkipFollowTagConfirm,
    setSkipFollowTagConfirm,
} from "@/utils/localStore";

export interface FollowTagDialogProps {
    /** Tag being confirmed; null → dialog closed. */
    tag: string | null;
    /** True when the viewer already follows the tag (→ unfollow flow). */
    following: boolean;
    /** Accent for the confirm button. */
    accentColor: string;
    accentDark?: string;
    onConfirm: () => void;
    onClose: () => void;
}

export default function FollowTagDialog({
    tag,
    following,
    accentColor,
    accentDark,
    onConfirm,
    onClose,
}: FollowTagDialogProps) {
    const { isAuthenticated } = useAuth();
    const [dontShowAgain, setDontShowAgain] = React.useState(false);

    // Reset the checkbox each time the dialog opens for a new tag.
    React.useEffect(() => {
        if (tag) setDontShowAgain(false);
    }, [tag]);

    // "Don't show again" bypass: signed-in users who opted out get the
    // toggle applied immediately — every caller routes the actual toggle
    // through onConfirm, so auto-confirming here covers all five pages
    // without touching their wiring. (The switch to re-enable the
    // confirmation lives in the profile modal.)
    const autoConfirmedRef = React.useRef<string | null>(null);
    React.useEffect(() => {
        if (!tag) {
            autoConfirmedRef.current = null;
            return;
        }
        if (
            isAuthenticated &&
            getSkipFollowTagConfirm() &&
            autoConfirmedRef.current !== tag
        ) {
            autoConfirmedRef.current = tag;
            onConfirm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tag, isAuthenticated]);

    const handleConfirm = () => {
        if (isAuthenticated && dontShowAgain) {
            setSkipFollowTagConfirm(true);
        }
        onConfirm();
    };

    if (tag && isAuthenticated && getSkipFollowTagConfirm()) {
        // Auto-confirm in flight — never flash the dialog.
        return null;
    }

    return (
        <Dialog
            open={!!tag}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            // Enter confirms; Escape cancels (Dialog's built-in onClose).
            onKeyDown={(event) => {
                if (event.key === "Enter") {
                    event.preventDefault();
                    handleConfirm();
                }
            }}
        >
            <DialogTitle>
                {following ? "Unfollow tag?" : "Follow tag?"}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary">
                    {following
                        ? `Stop following “${tag}”? It will be toggled off in your profile and no longer personalize your “Most relevant to you” recommendations.`
                        : `Follow “${tag}”? It will be toggled on in your profile and personalize your “Most relevant to you” recommendations.`}
                </Typography>
                {isAuthenticated && (
                    <FormControlLabel
                        sx={{ mt: 1.5 }}
                        control={
                            <Checkbox
                                size="small"
                                checked={dontShowAgain}
                                onChange={(e) =>
                                    setDontShowAgain(e.target.checked)
                                }
                            />
                        }
                        label={
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Don&apos;t show this confirmation again (you
                                can turn it back on in your profile)
                            </Typography>
                        }
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button color="inherit" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    sx={{
                        backgroundColor: accentColor,
                        "&:hover": {
                            backgroundColor: accentDark ?? accentColor,
                        },
                    }}
                >
                    {following ? "Unfollow" : "Follow"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
